import React from 'react';

export interface D3GanttItem<T=unknown> {
  id: string;
  label: string;
  startDate: Date;
  endDate: Date;
  groupId?: string;
  groupLabel?: string;
  color?: string;
  metadata?: T;
  href?: string; // optional link target
}

export interface D3GanttChartProps<T=unknown> {
  items: D3GanttItem<T>[];
  title?: string;
  description?: string;
  laneHeight?: number;
  legendWidth?: number;
  showItemLabelInBar?: boolean;
  legendMode?: 'group' | 'none';
  tooltipRender?: (item: D3GanttItem<T>) => React.ReactNode;
  // Optional: Force the visible x-domain [ms, ms]
  xDomainOverride?: [number, number];
  // Pixels per day used to compute scrollable width
  pxPerDay?: number;
  // Optional categorical epochs: each epoch occupies equal width in order
  epochs?: { id: string; label: string }[];
  // Optional milestones rendered as diamonds on epoch boundaries
  milestones?: { epochIndex: number; lane?: number; label: string; href?: string }[];
  // Explicit width (px) per epoch cell when epochs[] is provided
  epochWidthPx?: number;
  // Categorical index that represents 'today' for a unique guide line
  currentEpochIndex?: number;
  // If provided, positions the 'today' line inside the cell. Example: 1.5 means halfway through epoch index 1
  currentEpochPosition?: number;
  // Force each item onto its own lane; lanes are ordered by start then end
  oneItemPerRow?: boolean;
}

const pad = { top: 16, right: 24, bottom: 28, left: 16 };

export function D3GanttChart<T>({
  items,
  title = 'Gantt Chart',
  description = '',
  laneHeight = 26,
  legendWidth = 140,
  showItemLabelInBar = true,
  legendMode = 'group',
  tooltipRender,
  xDomainOverride,
  pxPerDay = 28,
  epochs,
  milestones = [],
  epochWidthPx = 96,
  currentEpochIndex,
  currentEpochPosition,
  oneItemPerRow = false,
}: D3GanttChartProps<T>) {
  // Group + lane pack
  const { lanes, laneLabels, laneAssignments, bands, minDate, maxDate } = React.useMemo(() => {
    // Sort items by start then end asc for stable ordering
    const sorted = [...items].sort((a,b)=> {
      const sa = a.startDate.valueOf();
      const sb = b.startDate.valueOf();
      if (sa !== sb) return sa - sb;
      return a.endDate.valueOf() - b.endDate.valueOf();
    });

    // If each item gets its own lane, build a flat lane assignment
    if (oneItemPerRow) {
      const laneAssignments = new Map<string, number>();
      sorted.forEach((it, idx) => laneAssignments.set(it.id, idx));
      const lanes = sorted.length;
      const laneLabels = sorted.map(it => it.label);
      const bands = [{ label: '', startLane: 0, endLane: Math.max(0, lanes-1) }];
      const minDate = sorted.length ? Math.min(...sorted.map(i=>i.startDate.valueOf())) : Date.now();
      const maxDate = sorted.length ? Math.max(...sorted.map(i=>i.endDate.valueOf())) : Date.now();
      return { lanes, laneLabels, laneAssignments, bands, minDate, maxDate };
    }

    const groups = new Map<string,{label:string, items: typeof items}>();
    for (const it of sorted) {
      const gid = it.groupId ?? '__default';
      const glabel = it.groupLabel ?? '';
      if (!groups.has(gid)) groups.set(gid,{label:glabel, items: [] as any});
      (groups.get(gid)!.items as any).push(it);
    }
    let globalLane = 0;
    const laneLabels: string[] = [];
    const laneAssignments = new Map<string, number>();
    const bands: {startLane:number,endLane:number,label:string}[] = [];
    let minDate = Number.POSITIVE_INFINITY;
    let maxDate = Number.NEGATIVE_INFINITY;
    for (const [,g] of groups) {
      const laneEnds: number[] = [];
      const startLane = globalLane;
      for (const it of g.items) {
        const s = it.startDate.valueOf();
        const e = it.endDate.valueOf();
        minDate = Math.min(minDate, s);
        maxDate = Math.max(maxDate, e);
        let lane = laneEnds.findIndex(end => end <= s);
        if (lane === -1) { lane = laneEnds.length; laneEnds.push(e); }
        else { laneEnds[lane] = e; }
        const y = globalLane + lane;
        laneAssignments.set(it.id, y);
        laneLabels[y] = it.label;
      }
      const endLane = globalLane + Math.max(0, laneEnds.length-1);
      if (g.items.length>0) bands.push({startLane, endLane, label: g.label});
      globalLane += laneEnds.length + 1; // spacer
    }
    const lanes = Math.max(0, globalLane-1);
    if (!isFinite(minDate) || !isFinite(maxDate)) {
      minDate = Date.now();
      maxDate = minDate + 24*3600*1000;
    }
    return { lanes, laneLabels, laneAssignments, bands, minDate, maxDate };
  }, [items, oneItemPerRow]);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState({ width: 800, height: Math.max(220, lanes*laneHeight+80) });
  const [tooltip, setTooltip] = React.useState<{ x: number; y: number; item: D3GanttItem<T> } | null>(null);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        // Account for milestone lanes below items
        const milestoneBase = lanes;
        const extra = milestones && milestones.length > 0 ? (1 + Math.max(0, ...milestones.map(m => m.lane ?? 0))) : 0;
        const totalLanes = lanes + extra;
        setSize({ width: w, height: Math.max(220, totalLanes*laneHeight+80) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [lanes, laneHeight, milestones]);

  const legendSpace = (legendMode === 'none') ? 0 : legendWidth;
  const viewportW = Math.max(0, size.width - legendSpace);
  // Categorical epoch mode vs continuous time
  const categorical = Array.isArray(epochs) && epochs.length > 0;
  let domainMin = xDomainOverride ? xDomainOverride[0] : minDate;
  let domainMax = xDomainOverride ? xDomainOverride[1] : maxDate;
  const dayMs = 24*60*60*1000;
  let plotScrollableW = 0;
  let chartWidth = 0;
  let innerW = 0;
  let epochW = 0;
  if (categorical) {
    // Equal spacing per epoch
    epochW = Math.max(24, Math.ceil(epochWidthPx));
    plotScrollableW = Math.max(viewportW - pad.left - pad.right, Math.ceil(epochs!.length * epochW));
    chartWidth = plotScrollableW + pad.left + pad.right;
    innerW = plotScrollableW;
  } else {
    const totalDays = Math.max(1, Math.ceil((domainMax - domainMin) / dayMs));
    plotScrollableW = Math.max(viewportW - pad.left - pad.right, Math.ceil(totalDays * pxPerDay));
    chartWidth = plotScrollableW + pad.left + pad.right;
    innerW = plotScrollableW;
  }
  const innerH = Math.max(0, size.height - pad.top - pad.bottom);

  // simple time scale
  // X positioning
  const xFor = React.useCallback((t:number) => {
    if (categorical) {
      // In categorical mode, t is treated as an epoch boundary index
      return pad.left + t * epochW;
    } else {
      const minX = domainMin;
      const maxX = domainMax;
      if (maxX === minX) return pad.left;
      const p = (t - minX) / (maxX - minX);
      return pad.left + p * innerW;
    }
  }, [categorical, domainMin, domainMax, innerW, epochW]);

  const yForLane = React.useCallback((lane:number) => pad.top + lane*laneHeight + laneHeight/2, [laneHeight]);

  // ticks weekly
  const ticks = React.useMemo(() => {
    if (categorical) {
      return epochs!.map((_, i) => i); // one tick per epoch boundary index
    }
    const out:number[] = [];
    const start = new Date(domainMin);
    start.setHours(0,0,0,0);
    const end = domainMax;
    while (start.valueOf() <= end) {
      out.push(start.valueOf());
      start.setDate(start.getDate()+7);
    }
    return out;
  }, [categorical, epochs, domainMin, domainMax]);

  return (
    <div className="w-full" ref={containerRef}>
      <div className="flex w-full h-full" style={{ height: size.height }}>
        {/* Left legend - groups only */}
        {legendMode !== 'none' && (
          <div className="shrink-0 text-xs text-gray-300 dark:text-gray-200" style={{ width: legendWidth, paddingTop: pad.top, paddingBottom: pad.bottom }}>
            {bands.map((b, i) => {
              const h = (b.endLane - b.startLane + 1) * laneHeight;
              return (
                <div key={i} className="px-2 flex items-center" style={{ height: h, transform: `translateY(${(b.startLane)*laneHeight}px)` }}>
                  <span className="font-medium truncate" title={b.label}>{b.label}</span>
                </div>
              );
            })}
          </div>
        )}
        {/* Chart */}
        <div className="relative gantt-scroll" style={{ width: viewportW, overflowX: 'auto', overflowY: 'hidden', scrollbarGutter: 'stable both-edges', paddingBottom: 18 }}>
        <svg width={chartWidth} height={size.height}>
          {/* grid & bands */}
          <g>
            {bands.map((b,i)=> (
              <rect key={i} x={pad.left} y={pad.top + b.startLane*laneHeight} width={innerW} height={(b.endLane - b.startLane + 1)*laneHeight} fill="currentColor" opacity={0.04} />
            ))}
            {/* vertical grid lines */}
            {categorical
              ? ticks.map((idx:number,i)=> (
                  <line key={i} x1={xFor(idx)} y1={pad.top} x2={xFor(idx)} y2={pad.top+innerH} stroke="currentColor" opacity={0.12} strokeDasharray="2 2" />
                ))
              : ticks.map((t,i)=> (
                  <line key={i} x1={xFor(t)} y1={pad.top} x2={xFor(t)} y2={pad.top+innerH} stroke="currentColor" opacity={0.12} />
                ))}
            {/* today */}
            {!categorical && (
              <line x1={xFor(Date.now())} y1={pad.top} x2={xFor(Date.now())} y2={pad.top+innerH} stroke="currentColor" strokeDasharray="3 3" opacity={0.5} />
            )}
            {categorical && (typeof currentEpochPosition === 'number' || typeof currentEpochIndex === 'number') && (
              <line x1={xFor((currentEpochPosition ?? currentEpochIndex!) )} y1={pad.top} x2={xFor((currentEpochPosition ?? currentEpochIndex!) )} y2={pad.top+innerH} stroke="#60a5fa" strokeDasharray="4 2" opacity={0.8} />
            )}
          </g>

          {/* axis labels */}
          <g>
            {categorical
              ? (epochs || []).map((e, i) => (
                  <text key={i} x={xFor(i)} y={pad.top+innerH+16} textAnchor="middle" fill="currentColor" opacity={0.8} fontSize={11}>
                    {e.label}
                  </text>
                ))
              : ticks.map((t,i)=> (
                  <text key={i} x={xFor(t)} y={pad.top+innerH+16} textAnchor="middle" fill="currentColor" opacity={0.8} fontSize={11}>
                    {new Date(t).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric'})}
                  </text>
                ))}
          </g>

          {/* bars */}
          <g>
            {items.map((it) => {
              const lane = laneAssignments.get(it.id) ?? 0;
              let x1 = 0, x2 = 0, w = 0;
              if (categorical) {
                // Interpret startDate/endDate as epoch indices stored in Date value (ms)
                const sIdx = it.startDate.valueOf();
                const eIdx = it.endDate.valueOf();
                const cellPad = 6;
                x1 = xFor(sIdx) + cellPad;
                x2 = xFor(eIdx + 1) - cellPad; // end at next boundary, with padding
                w = Math.max(2, x2 - x1);
              } else {
                x1 = xFor(it.startDate.valueOf());
                x2 = xFor(it.endDate.valueOf());
                w = Math.max(2, x2-x1);
              }
              const y = yForLane(lane);
              const barH = laneHeight-8;
              const hitW = Math.max(w, 10);
              const clipId = `clip-${it.id}`;
              const content = (
                <g key={it.id}>
                  <defs>
                    <clipPath id={clipId}>
                      {/* leave right-side 10px padding for label */}
                      <rect x={x1} y={y - barH/2} width={Math.max(0, w - 10)} height={barH} rx={6} ry={6} />
                    </clipPath>
                  </defs>
                  <a href={it.href} target={it.href ? '_blank' : undefined} rel={it.href ? 'noopener noreferrer' : undefined}>
                    {/* Hover hit area to improve tooltip reliability on narrow bars */}
                    <rect
                      x={x1 + (w < hitW ? -(hitW-w)/2 : 0)}
                      y={y - Math.max(barH, 14)/2}
                      width={hitW}
                      height={Math.max(barH, 14)}
                      fill="transparent"
                      onMouseEnter={(e) => tooltipRender && setTooltip({ x: e.clientX, y: e.clientY, item: it })}
                      onMouseMove={(e) => tooltipRender && setTooltip({ x: e.clientX, y: e.clientY, item: it })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                    <rect
                      x={x1}
                      y={y - barH/2}
                      width={w}
                      height={barH}
                      rx={6}
                      ry={6}
                      fill={it.color || '#111827'}
                      stroke="#1f2937"
                      opacity={0.95}
                      onMouseEnter={(e) => tooltipRender && setTooltip({ x: e.clientX, y: e.clientY, item: it })}
                      onMouseMove={(e) => tooltipRender && setTooltip({ x: e.clientX, y: e.clientY, item: it })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  </a>
                  {showItemLabelInBar && (
                    <text x={x1+8} y={y+4} fontSize={12} fontWeight={500} fill="#ffffff" pointerEvents="none" clipPath={`url(#${clipId})`}>
                      {truncate(it.label, Math.max(0, w - 12))}
                    </text>
                  )}
                </g>
              );
              return content;
            })}

            {/* milestones as diamonds with labels */}
            {categorical && milestones.map((m, i) => {
              // Align to completion boundary: end of epoch (right boundary line)
              const x = xFor(m.epochIndex + 1);
              const lane = lanes + (m.lane ?? 0);
              const y = yForLane(lane);
              const size = 8;
              const points = [
                `${x},${y - size/2}`,
                `${x + size/2},${y}`,
                `${x},${y + size/2}`,
                `${x - size/2},${y}`
              ].join(' ');
              return (
                <g key={`ms-${i}`}>
                  {m.href ? (
                    <a href={m.href} target="_blank" rel="noopener noreferrer">
                      <polygon points={points} fill="#f59e0b" stroke="#b45309" />
                      <text x={x + size + 4} y={y + 4} fontSize={12} fill="#f59e0b" fontWeight={600}>{m.label}</text>
                    </a>
                  ) : (
                    <>
                      <polygon points={points} fill="#f59e0b" stroke="#b45309" />
                      <text x={x + size + 4} y={y + 4} fontSize={12} fill="#f59e0b" fontWeight={600}>{m.label}</text>
                    </>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
        {tooltip && tooltipRender && (
          <div className="pointer-events-none absolute" style={{ left: tooltip.x + 8 - (containerRef.current?.getBoundingClientRect().left || 0) - legendSpace, top: tooltip.y + 8 - (containerRef.current?.getBoundingClientRect().top || 0) }}>
            {tooltipRender(tooltip.item)}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function truncate(text: string, maxWidth: number) {
  const charW = 6;
  const max = Math.floor(maxWidth/charW);
  if (max <= 3) return '';
  return text.length > max ? text.slice(0, max-3) + '...' : text;
}
